import type { Classroom } from './types';

// The class's live meeting (Google Meet, Zoom…): its link, and when it happens
// each week. The day and time are in the class's time zone; everyone sees the
// next meeting converted to their own clock.

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Only https links go behind the Join button. Returns the cleaned link, or null. */
export function cleanMeetUrl(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  try {
    const u = new URL(/^[a-z]+:\/\//i.test(s) ? s : `https://${s}`);
    return u.protocol === 'https:' && u.hostname.includes('.') ? u.toString() : null;
  } catch { return null; }
}

export function providerOf(url: string): { name: string; icon: string } {
  let host = '';
  try { host = new URL(url).hostname.toLowerCase(); } catch { /* fall through */ }
  if (host === 'meet.google.com') return { name: 'Google Meet', icon: '🟢' };
  if (host.endsWith('zoom.us') || host.endsWith('zoom.com')) return { name: 'Zoom', icon: '🔵' };
  if (host.endsWith('teams.microsoft.com') || host.endsWith('teams.live.com')) return { name: 'Microsoft Teams', icon: '🟣' };
  return { name: 'Video call', icon: '📹' };
}

/** Minutes the zone is ahead of UTC at a given instant. */
function zoneOffset(at: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(at));
  const v = (t: string) => Number(parts.find(p => p.type === t)?.value);
  return (Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second')) - at) / 60000;
}

/** The instant a wall-clock time happens in a zone (handles daylight saving). */
function zonedToInstant(y: number, m: number, d: number, hh: number, mm: number, tz: string): number {
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  let t = guess - zoneOffset(guess, tz) * 60000;
  t = guess - zoneOffset(t, tz) * 60000;
  return t;
}

export type NextMeeting = { start: Date; end: Date; live: boolean; minutesAway: number };

/** The meeting happening now, or the next one. Null if the class has no weekly time set. */
export function nextMeeting(c: Pick<Classroom, 'meet_day' | 'meet_time' | 'meet_minutes' | 'timezone'>, now = new Date()): NextMeeting | null {
  if (c.meet_day == null || !c.meet_time) return null;
  const tz = c.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [hh, mm] = c.meet_time.split(':').map(Number);
  const length = (c.meet_minutes ?? 60) * 60000;
  // Today's date in the class's zone, then step forward to the meeting day.
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const [y, m, d] = today.split('-').map(Number);
  for (let i = -1; i <= 7; i++) {
    const day = new Date(Date.UTC(y, m - 1, d + i));
    if (day.getUTCDay() !== c.meet_day) continue;
    const start = zonedToInstant(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), hh, mm, tz);
    if (start + length < now.getTime()) continue;   // already over
    return {
      start: new Date(start), end: new Date(start + length),
      live: now.getTime() >= start - 10 * 60000 && now.getTime() < start + length,
      minutesAway: Math.round((start - now.getTime()) / 60000),
    };
  }
  return null;
}

/** "Tuesdays at 4:00 PM", in the viewer's own time (it can be a different day for them). */
export function scheduleLine(c: Pick<Classroom, 'meet_day' | 'meet_time' | 'timezone' | 'meet_minutes'>): string | null {
  const n = nextMeeting(c);
  if (!n) return null;
  const day = n.start.toLocaleDateString(undefined, { weekday: 'long' });
  const time = n.start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day}s at ${time}`;
}

export function whenText(n: NextMeeting): string {
  if (n.live) return n.minutesAway > 0 ? `Starting in ${n.minutesAway} min` : 'Live now';
  if (n.minutesAway < 60) return `Starts in ${n.minutesAway} min`;
  if (n.minutesAway < 24 * 60) {
    const h = Math.floor(n.minutesAway / 60), m = n.minutesAway % 60;
    return `Starts in ${h} h${m ? ` ${m} min` : ''}`;
  }
  const day = n.start.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  return `Next: ${day}`;
}
